/**
 * Three.js scene parts for the 3D electrical-room view.
 *
 * CRITICAL: Everything in this file is constructed with `React.createElement`
 * (the `h` shorthand) NOT JSX. The project-wide visual-edits babel plugin
 * injects `x-line-number` props onto JSX elements which then get forwarded to
 * Three.js objects and throw `Cannot set "x-line-number"`. createElement
 * bypasses that AST transform.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useThree } from "@react-three/fiber";
import { OrbitControls, Grid, Html, Edges, Line } from "@react-three/drei";
import * as THREE from "three";

import ConduitLayerImpl from "./conduits";
import TrayLayerImpl from "./trays";
import { orientedDimensions } from "@/lib/orientation";

export { orientedDimensions };

export const IN_TO_M = 0.0254;
const h = React.createElement;
const ConduitLayer = ConduitLayerImpl;
const TrayLayer = TrayLayerImpl;
export { ConduitLayerImpl as ConduitLayer, TrayLayerImpl as TrayLayer };

/* ─── EquipmentBox ───────────────────────────────────────────────── */
/* `orientedDimensions` is imported from `@/lib/orientation` so the same
 * helper feeds BOTH the 3D scene AND the 2D Elevation Views (WallPanel),
 * keeping the two in lockstep after any flip / rotation. */

export function EquipmentBox({
  item, room, onDragEnd, selected, setSelected,
  dragPlaneRef, complianceById, snap, measureMode, onMeasurePick,
  onDragStart, onDragStop, locked, onContextMenu,
}) {
  const ref = useRef();
  const [dragging, setDragging] = useState(false);
  // Tracks whether the pointer actually moved past a small threshold while
  // the drag was active. A bare click (down + up with ≤ 3 px movement) leaves
  // this false, and we skip the position commit so the equipment doesn't
  // shift to the nearest snap-grid square on first click.
  const movedRef = useRef(false);
  const downPosRef = useRef({ x: 0, y: 0 });
  const { camera, gl } = useThree();
  const raycaster = useMemo(() => new THREE.Raycaster(), []);

  const pos = item.position || {};
  // v1.61 — pull world-frame dimensions through the shared orientedDimensions
  // helper so 2D + 3D agree after any rotation/flip combination. wallSize is
  // the horizontal extent along the wall, vertSize is vertical, thickness is
  // the wall-perpendicular extent. We render the box axis-aligned (no mesh
  // rotation) since the orientation is already baked into the dimensions.
  const { wallSize, vertSize, thickness } = orientedDimensions(item);
  const widthIn  = wallSize;
  const depthIn  = thickness;
  const heightIn = vertSize;
  // v2.12 — basement equipment uses NEGATIVE mount_height_in to position
  // it in the sub-floor pit. For all other surfaces we keep the >= 0 clamp.
  const isBasement = item.installation_side === "basement" || item.mount_surface === "basement";
  const mountIn = isBasement
    ? (Number(item.mount_height_in) || 0)
    : Math.max(0, Number(item.mount_height_in) || 0);
  const L = widthIn  * IN_TO_M;
  const W = depthIn  * IN_TO_M;
  const H = heightIn * IN_TO_M;
  const cx = ((pos.x_in || 0) + widthIn / 2) * IN_TO_M;
  const cz = ((pos.y_in || 0) + depthIn / 2) * IN_TO_M;
  const cy = (mountIn + heightIn / 2) * IN_TO_M;

  const isPlaced = !!item.position && pos.x_in !== undefined && pos.x_in >= 0;
  /* v1.82 — mounting bracket / stand-off rendering. The bracket sits
   * between the equipment's mounting face and the surface (wall or floor)
   * it's attached to. The surface is AUTO-DETECTED as whichever is
   * physically closest to the equipment — floor (y=0), or any of the four
   * walls. This gives correct behaviour for both wall- and floor-mounted
   * equipment without needing a separate "mount type" flag. (v1.81 used
   * mount_height_in as a proxy for mount type; that field is no longer
   * surfaced in the UI as of v1.82.) */
  const mntDepthIn = Math.max(0, Number(item.mounting_depth_in) || 0);
  let bracketProps = null;
  if (mntDepthIn > 0 && isPlaced && room) {
    const rL = room.length_in || 240;
    const rW = room.width_in  || 180;
    const brkM = mntDepthIn * IN_TO_M;
    // Distance from equipment's nearest face to each of the 5 candidate
    // mounting surfaces (4 walls + floor). Ceiling intentionally omitted
    // — equipment is almost never hung from the ceiling in EE rooms.
    const dFloor = mountIn;
    const dW = pos.x_in;
    const dE = rL - (pos.x_in + widthIn);
    const dN = pos.y_in;
    const dS = rW - (pos.y_in + depthIn);
    const minD = Math.min(dFloor, dW, dE, dN, dS);
    if (minD === dFloor) {
      // Floor-mounted — bracket sits BELOW equipment between its bottom
      // face and y = 0 (the floor). Same horizontal footprint as the
      // equipment so the bracket reads as a pedestal in 3D.
      bracketProps = {
        position: [cx, brkM / 2, cz],
        args:     [L,  brkM,     W],
        face: "floor",
      };
    } else if (minD === dW) {
      const xBack = pos.x_in * IN_TO_M;
      bracketProps = { position: [xBack - brkM / 2, cy, cz], args: [brkM, H, W], face: "W" };
    } else if (minD === dE) {
      const xFront = (pos.x_in + widthIn) * IN_TO_M;
      bracketProps = { position: [xFront + brkM / 2, cy, cz], args: [brkM, H, W], face: "E" };
    } else if (minD === dN) {
      const zBack = pos.y_in * IN_TO_M;
      bracketProps = { position: [cx, cy, zBack - brkM / 2], args: [L, H, brkM], face: "N" };
    } else {
      const zFront = (pos.y_in + depthIn) * IN_TO_M;
      bracketProps = { position: [cx, cy, zFront + brkM / 2], args: [L, H, brkM], face: "S" };
    }
  }
  const compliance = complianceById[item.id] || (isPlaced ? "ok" : "unplaced");
  const baseColor = compliance === "error"     ? "#FF3B5C"
                  : compliance === "warning"   ? "#FFCC00"
                  : compliance === "unplaced"  ? "#6b7280"
                  : selected === item.id       ? "#00E5FF"
                  : isBasement                 ? "#A78BFA"   // v2.12 — basement equipment violet
                  : "#3C82B4";

  const snapFn = (val_in) => snap > 0 ? Math.round(val_in / snap) * snap : val_in;

  const onPointerDown = (e) => {
    e.stopPropagation();
    if (measureMode) { onMeasurePick(item); return; }
    if (e.button !== undefined && e.button !== 0) return;
    setSelected(item.id);
    if (locked) {
      gl.domElement.style.cursor = "not-allowed";
      return;            // selection only — no drag while locked
    }
    movedRef.current = false;
    downPosRef.current = { x: e.clientX, y: e.clientY };
    setDragging(true);
    onDragStart && onDragStart();
    gl.domElement.style.cursor = "grabbing";
  };

  const onPointerUp = (e) => {
    if (!dragging) return;
    e.stopPropagation && e.stopPropagation();
    setDragging(false);
    onDragStop && onDragStop();
    gl.domElement.style.cursor = "auto";
    // Pure click (no real movement) → don't commit any position change. This
    // prevents the equipment from snap-shifting to the nearest grid square
    // when the user just clicked to select it.
    if (!movedRef.current) return;
    if (!ref.current) return;
    const world = new THREE.Vector3();
    ref.current.getWorldPosition(world);
    let newX = world.x / IN_TO_M - widthIn / 2;
    let newY = world.z / IN_TO_M - depthIn / 2;
    newX = Math.max(0, Math.min(room.length_in - widthIn, snapFn(newX)));
    newY = Math.max(0, Math.min(room.width_in  - depthIn, snapFn(newY)));
    // Snap-to-wall: if within 6" of a wall, lock flush against it.
    const WALL_SNAP_IN = 6;
    if (newX < WALL_SNAP_IN) newX = 0;
    else if (newX > room.length_in - widthIn - WALL_SNAP_IN) newX = room.length_in - widthIn;
    if (newY < WALL_SNAP_IN) newY = 0;
    else if (newY > room.width_in - depthIn - WALL_SNAP_IN) newY = room.width_in - depthIn;
    onDragEnd(item.id, Math.round(newX * 10) / 10, Math.round(newY * 10) / 10);
  };

  const onPointerMove = (e) => {
    if (!dragging || !ref.current) return;
    e.stopPropagation && e.stopPropagation();
    // Promote to "actual drag" only once the pointer has moved past ~3 px.
    if (!movedRef.current) {
      const dx = e.clientX - downPosRef.current.x;
      const dy = e.clientY - downPosRef.current.y;
      if (Math.hypot(dx, dy) < 3) return;
      movedRef.current = true;
    }
    const rect = gl.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    raycaster.setFromCamera(ndc, camera);
    if (!dragPlaneRef.current) return;
    const hit = raycaster.intersectObject(dragPlaneRef.current);
    if (hit.length > 0) {
      ref.current.position.x = hit[0].point.x;
      ref.current.position.z = hit[0].point.z;
    }
  };

  useEffect(() => {
    if (!dragging) return;
    const domEl = gl.domElement;
    const handleMove = (e) => onPointerMove(e);
    const handleUp   = (e) => onPointerUp(e);
    domEl.addEventListener("pointermove", handleMove);
    domEl.addEventListener("pointerup", handleUp);
    return () => {
      domEl.removeEventListener("pointermove", handleMove);
      domEl.removeEventListener("pointerup", handleUp);
    };
  }, [dragging]); // eslint-disable-line

  const labelDiv = h("div", {
    style: {
      background: selected === item.id ? "rgba(0,229,255,0.95)" : "rgba(5,6,8,0.85)",
      color: selected === item.id ? "#000" : "#FFF",
      fontFamily: "ui-monospace, monospace", fontSize: 10, fontWeight: 600,
      padding: "2px 6px", border: "1px solid rgba(0,229,255,0.35)",
      whiteSpace: "nowrap", pointerEvents: "none",
    },
  }, item.tag || item.equipment_type || "—");

  return h("group", null,
    bracketProps && h("mesh", {
      key: "mnt-bracket",
      position: bracketProps.position,
      castShadow: false, receiveShadow: false,
    },
      h("boxGeometry", { args: bracketProps.args }),
      h("meshStandardMaterial", {
        color: "#FF9F0A", metalness: 0.4, roughness: 0.5,
        emissive: "#3a2300", emissiveIntensity: 0.35,
        transparent: true, opacity: 0.85,
      }),
      h(Edges, { color: "#FFB347", threshold: 15 }),
    ),
    h("mesh", {
      ref,
      position: [cx, cy, cz],
      // No mesh rotation — yaw is already baked into wallSize/thickness/
      // vertSize via orientedDimensions(). Mesh stays axis-aligned so the
      // box's worldX matches the 2D widthOnWall exactly.
      onPointerDown,
      onContextMenu: (e) => {
        e.stopPropagation();
        // Native browser context menu still fires from the canvas, so we let
        // the parent decide whether to prevent it and pop our own menu.
        onContextMenu && onContextMenu(e, item);
      },
      onPointerOver: (e) => { e.stopPropagation(); gl.domElement.style.cursor = measureMode ? "crosshair" : "grab"; },
      onPointerOut: () => { if (!dragging) gl.domElement.style.cursor = "auto"; },
      castShadow: true,
    },
      h("boxGeometry", { args: [L, H, W] }),
      h("meshStandardMaterial", {
        color: baseColor, metalness: 0.25, roughness: 0.6,
        emissive: selected === item.id ? "#003a40" : "#000000",
        emissiveIntensity: selected === item.id ? 0.6 : 0,
        transparent: compliance === "unplaced", opacity: compliance === "unplaced" ? 0.55 : 1,
      }),
      h(Edges, { color: selected === item.id ? "#00E5FF" : "#0c1117", threshold: 15 }),
    ),
    h(Html, {
      position: [cx, (mountIn + heightIn) * IN_TO_M + 0.1, cz],
      center: true, distanceFactor: 6, zIndexRange: [10, 0], pointerEvents: "none",
      occlude: true,
    }, labelDiv),
  );
}

/* ─── Room ──────────────────────────────────────────────────────── */
export function Room({ room, dragPlaneRef, snap, showFloorGrid = true,
                       showWallGrids = false, showShell = false, openings = [],
                       hiddenWalls = [], onToggleWall = null }) {
  const L = room.length_in * IN_TO_M;
  const W = room.width_in  * IN_TO_M;
  const H = (room.depth_in || 96) * IN_TO_M;
  // v2.13 — wall + roof visibility is now derived from `project.hidden_walls`
  // (passed in as `hiddenWalls` prop). This makes wall hide/show go through
  // the normal `update()` path → captured by the project-level undo stack
  // so Ctrl+Z restores a hidden wall. Shell presets just write the right
  // hidden_walls array (Structural=[], Presentation=["C"]).
  // v2.15 — extended to cover FLOOR + the basement pit walls/floor so the
  // user can also right-click those to hide them and Ctrl+Z to restore.
  // IDs: A B C D ROOF FLOOR B_A B_B B_C B_D B_FLOOR.
  const hiddenSet = new Set(hiddenWalls || []);
  const wallViz = {
    A:        !hiddenSet.has("A"),
    B:        !hiddenSet.has("B"),
    C:        !hiddenSet.has("C"),
    D:        !hiddenSet.has("D"),
    ROOF:     !hiddenSet.has("ROOF"),
    FLOOR:    !hiddenSet.has("FLOOR"),
    B_A:      !hiddenSet.has("B_A"),
    B_B:      !hiddenSet.has("B_B"),
    B_C:      !hiddenSet.has("B_C"),
    B_D:      !hiddenSet.has("B_D"),
    B_FLOOR:  !hiddenSet.has("B_FLOOR"),
  };
  const toggleWallViz = (k) => (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (e && e.nativeEvent && e.nativeEvent.preventDefault) e.nativeEvent.preventDefault();
    if (onToggleWall) onToggleWall(k);
  };
  const cellIn = snap > 0 ? snap : 12;
  const cellSize = IN_TO_M * cellIn;

  const gridProps = {
    cellSize,
    cellThickness: snap > 0 ? 0.8 : 0.5,
    cellColor: snap > 0 ? "#27313f" : "#1A1F29",
    sectionSize: IN_TO_M * 60,
    sectionThickness: 1,
    sectionColor: "#00E5FF",
    fadeDistance: Math.max(L, W, H) * 2.5,
    infiniteGrid: false,
    side: THREE.DoubleSide,
  };

  // v2.08 — Shell walls now SOLID (opacity 1, depthWrite true) so they
  // properly occlude equipment / trays behind them. The previous opacity
  // 0.45 transparency was the reason users could see "through" the shell
  // even with showShell=true.
  const wallMat = h("meshStandardMaterial", {
    color: "#8B8F97", transparent: false, opacity: 1,
    side: THREE.DoubleSide, depthWrite: true,
    metalness: 0.1, roughness: 0.75,
  });

  /* Wall labels A / B / C / D at the centre of each wall.
     A = Back (z=W) · B = Right (x=L) · C = Front (z=0) · D = Left (x=0). */
  const WALL_LABEL_COLOR = "#FFCC00";
  const wallLabelStyle = {
    background: WALL_LABEL_COLOR, color: "#0A0C10",
    fontFamily: "ui-monospace,monospace", fontSize: 14, fontWeight: 800,
    padding: "3px 10px", border: "2px solid #0A0C10",
    letterSpacing: 2,
  };
  const wallLabels = h("group", { key: "wall-labels" },
    h(Html, { position: [L / 2, H * 0.6, W - 0.02], center: true, distanceFactor: 12, pointerEvents: "none" },
      h("div", { style: wallLabelStyle }, "WALL A")),
    h(Html, { position: [L - 0.02, H * 0.6, W / 2], center: true, distanceFactor: 12, pointerEvents: "none" },
      h("div", { style: wallLabelStyle }, "WALL B")),
    h(Html, { position: [L / 2, H * 0.6, 0.02], center: true, distanceFactor: 12, pointerEvents: "none" },
      h("div", { style: wallLabelStyle }, "WALL C")),
    h(Html, { position: [0.02, H * 0.6, W / 2], center: true, distanceFactor: 12, pointerEvents: "none" },
      h("div", { style: wallLabelStyle }, "WALL D")),
  );

  /** Build a per-wall grid as a LineSegments BufferGeometry — works
   *  reliably on any rotation, unlike drei's <Grid /> shader. The minor
   *  cells use a dim grey, the 60-inch section lines use the brand cyan. */
  const buildWallGridGeo = (widthM, heightM, pitchM) => {
    const minor = [];
    const major = [];
    const eps = 0.0005;          // tiny tolerance so the edges count
    const sectionEvery = 5;      // every 5 cells = 60"  (if pitch=12") OR 5*6=30" (if 6")
    let i = 0;
    for (let x = 0; x <= widthM + eps; x += pitchM, i++) {
      const buf = (i % sectionEvery === 0) ? major : minor;
      buf.push(x, 0, 0, x, heightM, 0);
    }
    let j = 0;
    for (let y = 0; y <= heightM + eps; y += pitchM, j++) {
      const buf = (j % sectionEvery === 0) ? major : minor;
      buf.push(0, y, 0, widthM, y, 0);
    }
    const mk = (arr) => {
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.Float32BufferAttribute(arr, 3));
      return g;
    };
    return { minor: mk(minor), major: mk(major) };
  };

  /** A single wall grid placed at `origin` with the given rotation. The
   *  grid is defined in local XY coordinates from (0,0) → (width, height). */
  const wallGridMesh = (key, widthM, heightM, position, rotation) => {
    const { minor, major } = buildWallGridGeo(widthM, heightM, cellSize);
    return h("group", { key, position, rotation },
      h("lineSegments", { geometry: minor },
        h("lineBasicMaterial", { color: "#27313f", transparent: true, opacity: 0.85 }),
      ),
      h("lineSegments", { geometry: major },
        h("lineBasicMaterial", { color: "#00E5FF", transparent: true, opacity: 0.55 }),
      ),
    );
  };

  /** Return [planeArgs, positionForOpening] for an opening on a given wall. */
  const openingPos = (op) => {
    // op: { wall, x_in (along the wall left→right), y_in (bottom from floor), w_in, h_in, kind }
    const wallId = (op.wall || "back").toLowerCase();
    const w = (op.w_in || 36) * IN_TO_M;
    const ht = (op.h_in || 80) * IN_TO_M;
    const xOff = ((op.x_in || 0) + (op.w_in || 36) / 2) * IN_TO_M;
    const yOff = ((op.y_in || 0) + (op.h_in || 80) / 2) * IN_TO_M;
    // Slight inset so the opening plane peeks through the wall without z-fighting.
    const inset = 0.01;
    switch (wallId) {
      case "back":   return { args: [w, ht], pos: [xOff, yOff, W + inset],  rot: [0, 0, 0] };
      case "front":  return { args: [w, ht], pos: [xOff, yOff, -inset],     rot: [0, Math.PI, 0] };
      case "left":   return { args: [w, ht], pos: [-inset, yOff, xOff],     rot: [0, -Math.PI / 2, 0] };
      case "right":  return { args: [w, ht], pos: [L + inset, yOff, xOff],  rot: [0, Math.PI / 2, 0] };
      default:       return null;
    }
  };

  const openingColor = (kind) => kind === "window" ? "#9bd9ff"
                              : kind === "cutout" ? "#0A0C10"
                              : "#3a2a18";     // door — wood-ish dark

  const openingMeshes = (openings || []).map((op, idx) => {
    const desc = openingPos(op);
    if (!desc) return null;
    return h("mesh", {
      key: `op-${idx}`, position: desc.pos, rotation: desc.rot,
    },
      h("planeGeometry", { args: desc.args }),
      h("meshStandardMaterial", {
        color: showShell ? openingColor(op.kind) : "#FFCC00",
        transparent: true,
        opacity: op.kind === "window" ? 0.55 : (showShell ? 1 : 0.85),
        side: THREE.DoubleSide,
        emissive: showShell ? "#000" : "#FFCC00",
        emissiveIntensity: showShell ? 0 : 0.4,
      }),
      h(Edges, { color: "#00E5FF", threshold: 1 }),
    );
  }).filter(Boolean);

  return h("group", null,
    // Floor
    // v2.15 — Room floor is now part of the SHELL: hidden when showShell
    // is OFF (so the user can see equipment/basement from underneath) and
    // right-click toggleable like the walls when ON. Always renders the
    // dark backdrop plane underneath so the scene doesn't show pure void
    // when Shell is off — keeps the orientation grid usable.
    h("mesh", { receiveShadow: true, position: [L / 2, -0.0002, W / 2], rotation: [-Math.PI / 2, 0, 0] },
      h("planeGeometry", { args: [L * 2.4, W * 2.4] }),
      h("meshStandardMaterial", { color: "#080a10" }),
    ),
    (showShell && wallViz.FLOOR) ? h("mesh", {
      receiveShadow: true, position: [L / 2, 0, W / 2],
      rotation: [-Math.PI / 2, 0, 0],
      onContextMenu: toggleWallViz("FLOOR"),
    },
      h("planeGeometry", { args: [L, W] }),
      h("meshStandardMaterial", { color: "#0d1117" }),
    ) : null,
    // v2.12 — Basement pit. v2.15 — basement walls + floor are now FULL
    // members of the shell: gated by showShell, each individually right-
    // click hideable (B_A/B_B/B_C/B_D/B_FLOOR), and they share the same
    // `wallMat` as the room walls when Shell is ON so the visual treatment
    // matches. With Shell OFF only the violet outline edges remain so the
    // pit boundary is still discoverable without occluding the equipment.
    (room.basement_depth_in || 0) > 0 ? (() => {
      const Bd = (room.basement_depth_in || 0) * IN_TO_M;
      return h("group", { key: "basement-pit" },
        // Basement floor slab — right-click hideable + only when Shell ON
        // so the user can peer "through" with Shell off.
        (showShell && wallViz.B_FLOOR) ? h("mesh", {
          position: [L / 2, -Bd, W / 2],
          rotation: [-Math.PI / 2, 0, 0],
          receiveShadow: true,
          onContextMenu: toggleWallViz("B_FLOOR"),
        },
          h("planeGeometry", { args: [L, W] }),
          wallMat,
        ) : null,
        // 4 basement walls — match the room wall convention so labels
        // line up: B_A = back (z=W), B_B = right (x=L), B_C = front (z=0),
        // B_D = left (x=0). wallMat is the shared room shell material so
        // the basement gets the SAME "covered" treatment as the room.
        (showShell && wallViz.B_A) ? h("mesh", {
          position: [L / 2, -Bd / 2, W],
          rotation: [0, 0, 0],
          onContextMenu: toggleWallViz("B_A"),
        }, h("planeGeometry", { args: [L, Bd] }), wallMat) : null,
        (showShell && wallViz.B_C) ? h("mesh", {
          position: [L / 2, -Bd / 2, 0],
          rotation: [0, Math.PI, 0],
          onContextMenu: toggleWallViz("B_C"),
        }, h("planeGeometry", { args: [L, Bd] }), wallMat) : null,
        (showShell && wallViz.B_D) ? h("mesh", {
          position: [0, -Bd / 2, W / 2],
          rotation: [0, -Math.PI / 2, 0],
          onContextMenu: toggleWallViz("B_D"),
        }, h("planeGeometry", { args: [W, Bd] }), wallMat) : null,
        (showShell && wallViz.B_B) ? h("mesh", {
          position: [L, -Bd / 2, W / 2],
          rotation: [0, Math.PI / 2, 0],
          onContextMenu: toggleWallViz("B_B"),
        }, h("planeGeometry", { args: [W, Bd] }), wallMat) : null,
        // Violet outline edges — always render so the pit boundary is
        // unmistakable even with Shell OFF or with walls hidden.
        h("mesh", { position: [L / 2, -Bd / 2, W / 2] },
          h("boxGeometry", { args: [L, Bd, W] }),
          h("meshBasicMaterial", { transparent: true, opacity: 0 }),
          h(Edges, { color: "#A78BFA", threshold: 1 }),
        ),
      );
    })() : null,
    // Invisible drag plane
    h("mesh", { ref: dragPlaneRef, position: [L / 2, 0.001, W / 2], rotation: [-Math.PI / 2, 0, 0], visible: false },
      h("planeGeometry", { args: [L * 2, W * 2] }),
      h("meshBasicMaterial", { transparent: true, opacity: 0 }),
    ),

    // v2.08 — Solid shell walls + roof, each individually right-click
    // toggleable. Wall A = back (z=W), B = right (x=L), C = front (z=0),
    // D = left (x=0), ROOF = top (y=H).
    showShell ? h("group", { key: "shell" },
      wallViz.A ? h("mesh", { position: [L / 2, H / 2, W], rotation: [0, 0, 0],
                              onContextMenu: toggleWallViz("A") },
        h("planeGeometry", { args: [L, H] }), wallMat) : null,
      wallViz.C ? h("mesh", { position: [L / 2, H / 2, 0], rotation: [0, Math.PI, 0],
                              onContextMenu: toggleWallViz("C") },
        h("planeGeometry", { args: [L, H] }), wallMat) : null,
      wallViz.D ? h("mesh", { position: [0, H / 2, W / 2], rotation: [0, -Math.PI / 2, 0],
                              onContextMenu: toggleWallViz("D") },
        h("planeGeometry", { args: [W, H] }), wallMat) : null,
      wallViz.B ? h("mesh", { position: [L, H / 2, W / 2], rotation: [0, Math.PI / 2, 0],
                              onContextMenu: toggleWallViz("B") },
        h("planeGeometry", { args: [W, H] }), wallMat) : null,
      wallViz.ROOF ? h("mesh", { position: [L / 2, H, W / 2], rotation: [Math.PI / 2, 0, 0],
                                 onContextMenu: toggleWallViz("ROOF") },
        h("planeGeometry", { args: [L, W] }), wallMat) : null,
    ) : null,

    // Wall labels A / B / C / D — always visible (even when shell off)
    wallLabels,

    // Openings (doors / windows / cutouts) — always render so users can see
    // where they sit even when the shell is toggled off.
    ...openingMeshes,

    // Room outline cube
    h("mesh", { position: [L / 2, H / 2, W / 2] },
      h("boxGeometry", { args: [L, H, W] }),
      h("meshBasicMaterial", { transparent: true, opacity: 0 }),
      h(Edges, { color: "#00E5FF", threshold: 1 }),
    ),

    // Floor grid (drei <Grid> handles horizontal planes cleanly)
    showFloorGrid ? h(Grid, { position: [L / 2, 0.002, W / 2], args: [L, W], ...gridProps }) : null,

    // Wall grids — hand-built LineSegments so they always render reliably.
    // Each wall's local (0,0) sits at one corner of the wall; (width,height)
    // sits at the opposite corner. The rotation maps local +X / +Y onto the
    // world axes that span the wall, with local +X / +Y staying inside the
    // room footprint.
    showWallGrids ? h("group", null,
      // Back wall  (z = W)  — local +X → world +X, local +Y → world +Y
      wallGridMesh("wg-back",  L, H, [0, 0, W - 0.002], [0, 0, 0]),
      // Front wall (z = 0)  — rotate π around Y so local +X spans from x=L back to 0
      wallGridMesh("wg-front", L, H, [L, 0, 0.002], [0, Math.PI, 0]),
      // Left wall  (x = 0)  — rotate −π/2 around Y so local +X → world +Z
      wallGridMesh("wg-left",  W, H, [0.002, 0, 0], [0, -Math.PI / 2, 0]),
      // Right wall (x = L)  — rotate +π/2 around Y so local +X → world −Z (starts at z=W)
      wallGridMesh("wg-right", W, H, [L - 0.002, 0, W], [0, Math.PI / 2, 0]),
      // Ceiling (y = H) — rotate π/2 around X so local +Y → world +Z
      wallGridMesh("wg-ceil",  L, W, [0, H - 0.002, 0], [Math.PI / 2, 0, 0]),
    ) : null,
  );
}

/* ─── WorkingSpaceLayer ─────────────────────────────────────────── */
/* NEC 110.26(A)(1) — minimum clearance in front of electrical equipment, sized by
   nominal voltage to ground (here we use the project voltage as a proxy):
     0-150 V Cond 1 → 36" ;   151-600 V Cond 1 → 36" ;   601-2500 V Cond 1 → 48".
   We assume Condition 1 (no exposed live parts on opposite side). */
function _workingSpaceIn(voltage) {
  if (voltage > 2500) return 60;
  if (voltage > 600)  return 48;
  return 36;
}

export function WorkingSpaceLayer({ items, voltage, room }) {
  const wsIn = _workingSpaceIn(voltage || 480);
  if (!items?.length) return null;
  const planes = items.map((it) => {
    if (!it.position || it._unplaced) return null;
    const p = it.position;
    const widthIn = p.w_in || it.length_in || 24;
    const depthIn = p.h_in || it.width_in  || 18;
    // The "front" of a panel is the side facing INTO the room. If the unit is in
    // the back half of the room (y > room/2) we assume it's mounted against the
    // back wall and the clearance extends toward the centre (lower y).
    const againstBack = p.y_in > room.width_in / 2;
    const clearY = againstBack ? Math.max(0, p.y_in - wsIn) : (p.y_in + depthIn);
    const clearH = Math.min(wsIn, againstBack ? p.y_in : room.width_in - (p.y_in + depthIn));
    if (clearH <= 0) return null;
    const cx = (p.x_in + widthIn / 2) * IN_TO_M;
    const cz = (clearY + clearH / 2) * IN_TO_M;
    const cy = 0.02;
    return h("group", { key: `ws-${it.id}` },
      h("mesh", { position: [cx, cy, cz], rotation: [-Math.PI / 2, 0, 0] },
        h("planeGeometry", { args: [widthIn * IN_TO_M, clearH * IN_TO_M] }),
        h("meshBasicMaterial", { color: "#FFCC00", transparent: true, opacity: 0.18, side: THREE.DoubleSide }),
      ),
      h(Html, { position: [cx, 0.05, cz], center: true, distanceFactor: 8, pointerEvents: "none" },
        h("div", {
          style: {
            background: "#FFCC00", color: "#0A0C10",
            fontFamily: "ui-monospace,monospace", fontSize: 9, fontWeight: 700,
            padding: "1px 5px", border: "1px solid rgba(0,0,0,0.5)",
            whiteSpace: "nowrap",
          },
        }, `WS ≥ ${wsIn}"`),
      ),
    );
  }).filter(Boolean);
  return h("group", null, ...planes);
}



/* ─── DimensionOverlay ──────────────────────────────────────────── */
/* For a single selected item: color-coded distance lines to each wall OR
 * the nearest neighboring equipment on the same axis, plus a roof clearance line.
 * Colors must match EquipmentSidePanel's inline editor.                          */
export const DIM_COLORS = {
  W: "#7CFFB2",  // left (toward x=0)        — lime
  E: "#FF66E0",  // right (toward x=L)       — magenta
  N: "#00E5FF",  // front (toward y=0)       — cyan
  S: "#FFCC00",  // back  (toward y=W)       — amber
  ROOF: "#B197FC", // ceiling                 — violet
  FLOOR: "#FFA66B", // floor distance         — orange (mount height)
};

/** Compute clearances for an item: distance to each wall AND to nearest
 *  neighbor on that side, whichever is smaller. Returns inches. */
export function computeClearances(item, room, allItems = []) {
  if (!item?.position) return null;
  const p = item.position;
  // v1.61 — use the shared orientation helper so clearances are computed
  // against the box's ACTUAL world-frame extents (which honour
  // orientation_flip + rotation_deg). Fixes the v1.60 bug where flipping
  // an equipment in 3D produced bogus distance measurements.
  const { wallSize, vertSize, thickness } = orientedDimensions(item);
  const w = wallSize;       // worldX extent
  const d = thickness;      // worldZ extent
  const h_eq = vertSize;    // worldY extent
  const mount = Math.max(0, Number(item.mount_height_in) || 0);
  const rL = room.length_in, rW = room.width_in;
  const rH = room.depth_in || 96;

  // Distances to walls
  const wW = p.x_in;                 // toward x=0 (left)
  const wE = rL - (p.x_in + w);      // toward x=rL (right)
  const wN = p.y_in;                 // toward y=0 (front)
  const wS = rW - (p.y_in + d);      // toward y=rW (back)
  const wRoof = Math.max(0, rH - (mount + h_eq));

  // Find nearest neighbor on each side (must overlap on the orthogonal axis).
  let nW = null, nE = null, nN = null, nS = null;
  for (const ot of allItems) {
    if (ot.id === item.id || !ot.position) continue;
    const op = ot.position;
    const otDim = orientedDimensions(ot);
    const ow = otDim.wallSize;
    const od = otDim.thickness;
    // Overlap on Y? (for east/west neighbors)
    const yOverlap = !(op.y_in + od < p.y_in || op.y_in > p.y_in + d);
    if (yOverlap) {
      // East: ot is to the right of this item → op.x_in > p.x_in
      if (op.x_in >= p.x_in + w) {
        const dist = op.x_in - (p.x_in + w);
        if (nE == null || dist < nE.dist) nE = { dist, item: ot };
      } else if (op.x_in + ow <= p.x_in) {
        const dist = p.x_in - (op.x_in + ow);
        if (nW == null || dist < nW.dist) nW = { dist, item: ot };
      }
    }
    // Overlap on X? (for north/south neighbors)
    const xOverlap = !(op.x_in + ow < p.x_in || op.x_in > p.x_in + w);
    if (xOverlap) {
      if (op.y_in >= p.y_in + d) {
        const dist = op.y_in - (p.y_in + d);
        if (nS == null || dist < nS.dist) nS = { dist, item: ot };
      } else if (op.y_in + od <= p.y_in) {
        const dist = p.y_in - (op.y_in + od);
        if (nN == null || dist < nN.dist) nN = { dist, item: ot };
      }
    }
  }

  const pick = (wall, neighbor) =>
    neighbor && neighbor.dist < wall
      ? { dist: neighbor.dist, target: neighbor.item.tag || neighbor.item.equipment_type || "EQ", kind: "neighbor" }
      : { dist: wall, target: "wall", kind: "wall" };

  return {
    W: pick(wW, nW),
    E: pick(wE, nE),
    N: pick(wN, nN),
    S: pick(wS, nS),
    ROOF: { dist: wRoof, target: "ceiling", kind: "ceiling" },
  };
}

export function DimensionOverlay({ item, room, allItems = [] }) {
  const cl = computeClearances(item, room, allItems);
  if (!cl) return null;
  const p = item.position;
  // v1.61 — pull oriented dimensions through the shared helper (same as
  // computeClearances) so the dimension overlay's bounding box matches
  // the rendered EquipmentBox after flip/rotation.
  const { wallSize, vertSize, thickness } = orientedDimensions(item);
  const w = wallSize;
  const d = thickness;
  const h_eq = vertSize;
  const mount = Math.max(0, Number(item.mount_height_in) || 0);
  const cx = (p.x_in + w / 2) * IN_TO_M;
  const cz = (p.y_in + d / 2) * IN_TO_M;
  const y0 = 0.02;

  const segs = [
    // West (toward x=0)
    { key: "W", from: [(p.x_in) * IN_TO_M, y0, cz], to: [Math.max(0, (p.x_in - cl.W.dist)) * IN_TO_M, y0, cz],
      color: DIM_COLORS.W, label: `W ${cl.W.dist.toFixed(0)}" → ${cl.W.target}` },
    // East
    { key: "E", from: [(p.x_in + w) * IN_TO_M, y0, cz], to: [Math.min(room.length_in, (p.x_in + w + cl.E.dist)) * IN_TO_M, y0, cz],
      color: DIM_COLORS.E, label: `E ${cl.E.dist.toFixed(0)}" → ${cl.E.target}` },
    // North (toward y=0)
    { key: "N", from: [cx, y0, (p.y_in) * IN_TO_M], to: [cx, y0, Math.max(0, (p.y_in - cl.N.dist)) * IN_TO_M],
      color: DIM_COLORS.N, label: `N ${cl.N.dist.toFixed(0)}" → ${cl.N.target}` },
    // South
    { key: "S", from: [cx, y0, (p.y_in + d) * IN_TO_M], to: [cx, y0, Math.min(room.width_in, (p.y_in + d + cl.S.dist)) * IN_TO_M],
      color: DIM_COLORS.S, label: `S ${cl.S.dist.toFixed(0)}" → ${cl.S.target}` },
    // Floor (vertical from floor to bottom of equipment) — only shown when raised
    ...(mount > 0 ? [{
      key: "FLOOR",
      from: [cx, 0, cz],
      to:   [cx, mount * IN_TO_M, cz],
      color: DIM_COLORS.FLOOR, label: `Floor ${mount.toFixed(0)}"`,
    }] : []),
    // Roof (vertical from top of equipment to ceiling)
    { key: "ROOF",
      from: [cx, (mount + h_eq) * IN_TO_M, cz],
      to:   [cx, (mount + h_eq + cl.ROOF.dist) * IN_TO_M, cz],
      color: DIM_COLORS.ROOF, label: `Roof ${cl.ROOF.dist.toFixed(0)}"` },
  ];

  return h("group", null, ...segs.map((ln) => {
    const geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(...ln.from), new THREE.Vector3(...ln.to),
    ]);
    const mx = (ln.from[0] + ln.to[0]) / 2;
    const my = (ln.from[1] + ln.to[1]) / 2 + 0.04;
    const mz = (ln.from[2] + ln.to[2]) / 2;
    return h("group", { key: `dim-${ln.key}` },
      h("line", { geometry: geo },
        h("lineBasicMaterial", { color: ln.color, linewidth: 2 }),
      ),
      h(Html, { position: [mx, my, mz], center: true, distanceFactor: 8, pointerEvents: "none" },
        h("div", {
          style: {
            background: ln.color,
            color: "#0A0C10", fontFamily: "ui-monospace,monospace",
            fontSize: 9, padding: "1px 6px", fontWeight: 700,
            border: "1px solid rgba(0,0,0,0.45)", whiteSpace: "nowrap",
            boxShadow: "0 0 0 1px rgba(255,255,255,0.15)",
          },
        }, ln.label),
      ),
    );
  }));
}


/* ─── TrayHandlesLayer ────────────────────────────────────────────── */
/* Renders a draggable yellow sphere at every tray segment endpoint
   (skipping fittings, which are points anchored to the plan). Dragging a
   handle calls `onMoveEndpoint(segId, pointIdx, [x_in, y_in])`. We raycast
   against a horizontal plane at the tray's ceiling-mount height so the
   handles slide on the ceiling — matching how trays are anchored. */
export function TrayHandlesLayer({ project, onMoveEndpoint, setOrbitEnabled, selectedTrayId }) {
  // Hooks MUST run unconditionally on every render — early-returns after hooks
  // only. The actual rendering is gated below.
  const planeRef = useRef();
  const dragRef = useRef(null);
  const camera = useThree((s) => s.camera);
  const raycaster = useThree((s) => s.raycaster);
  const gl = useThree((s) => s.gl);

  const room_H_in = project.depth_in || 96;
  const room_L_in = project.length_in || 240;
  const room_W_in = project.width_in  || 180;
  const ceilingMountY = (room_H_in - 12) * IN_TO_M;
  const DEFAULT_DROP_BOTTOM_IN = 78;

  /** Y-elevation in metres of a tray endpoint (point index 0 = top, 1 = bottom). */
  const endpointY = (s, idx) => {
    if (s.kind === "drop") {
      const top = (s.top_in != null) ? s.top_in : (room_H_in - 12);
      const bot = (s.bottom_in != null) ? s.bottom_in : DEFAULT_DROP_BOTTOM_IN;
      return (idx === 0 ? top : bot) * IN_TO_M;
    }
    const elev = (s.elevation_in != null) ? s.elevation_in : (room_H_in - 12);
    return elev * IN_TO_M;
  };

  const ndcFromEvent = (clientX, clientY) => {
    const rect = gl.domElement.getBoundingClientRect();
    return new THREE.Vector2(
      ((clientX - rect.left) / rect.width)  * 2 - 1,
      -((clientY - rect.top)  / rect.height) * 2 + 1,
    );
  };

  // Window-level pointer subscription — must run unconditionally per Rules of Hooks.
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const ndc = ndcFromEvent(e.clientX, e.clientY);
      raycaster.setFromCamera(ndc, camera);
      if (!planeRef.current) return;
      const hits = raycaster.intersectObject(planeRef.current);
      if (!hits.length) return;
      const p = hits[0].point;
      // Half-width margin (captured on pointer-down) so the tray cross-section
      // never pokes through a wall.
      const margin = dragRef.current.margin || 0;
      const x_in = Math.max(margin, Math.min(room_L_in - margin, p.x / IN_TO_M));
      const y_in = Math.max(margin, Math.min(room_W_in - margin, p.z / IN_TO_M));
      if (typeof onMoveEndpoint === "function") {
        onMoveEndpoint(dragRef.current.segId, dragRef.current.pointIdx,
                        [Math.round(x_in), Math.round(y_in)]);
      }
    };
    const onUp = (e) => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setOrbitEnabled && setOrbitEnabled(true);
      gl.domElement.releasePointerCapture && gl.domElement.releasePointerCapture(e.pointerId);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup",   onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup",   onUp);
    };
  }, [camera, raycaster, gl, room_L_in, room_W_in, onMoveEndpoint, setOrbitEnabled]);

  const segs = project.cable_trays || [];
  if (segs.length === 0 || typeof onMoveEndpoint !== "function") return null;

  const onDown = (segId, pointIdx, widthIn) => (e) => {
    e.stopPropagation();
    dragRef.current = {
      segId, pointIdx,
      margin: Math.max(0, (Number(widthIn) || 12) / 2),
    };
    setOrbitEnabled && setOrbitEnabled(false);
    gl.domElement.setPointerCapture && gl.domElement.setPointerCapture(e.pointerId);
  };

  const handles = [];
  segs.forEach((s) => {
    if (s.kind === "fitting") return;
    if (!s.points || s.points.length < 1) return;
    const isSelected = selectedTrayId === s.id;
    // Drops only need a single (x,y) plan-position but two Y elevations.
    const planPoints = s.kind === "drop"
      ? [s.points[0], s.points[0]]
      : s.points;
    if (planPoints.length < 2) return;
    planPoints.forEach(([x, y], i) => {
      const yMetres = endpointY(s, i);
      const label = i === 0 ? "A" : "B";
      const labelColor = i === 0 ? "#a3e635" : "#FF66E0";
      handles.push(
        h("group", { key: `h-${s.id}-${i}`, position: [x * IN_TO_M, yMetres + 0.04, y * IN_TO_M] },
          h("mesh", {
            onPointerDown: onDown(s.id, i, s.width_in),
            onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = "grab"; },
            onPointerOut: () => { document.body.style.cursor = "auto"; },
          },
            h("sphereGeometry", { args: [isSelected ? 0.08 : 0.06, 16, 12] }),
            h("meshStandardMaterial", {
              color: isSelected ? "#FFCC00" : "#FFCC00",
              emissive: "#FFCC00",
              emissiveIntensity: isSelected ? 0.9 : 0.4,
            }),
          ),
          // A/B label rendered as an HTML overlay anchored to the sphere
          h(Html, {
            position: [0, 0.10, 0],
            center: true,
            style: { pointerEvents: "none" },
            distanceFactor: 4,
          },
            React.createElement(
              "div",
              {
                style: {
                  color: labelColor,
                  background: "#0A0C10",
                  border: `1.5px solid ${labelColor}`,
                  borderRadius: "50%",
                  width: "18px", height: "18px",
                  lineHeight: "15px", textAlign: "center",
                  fontWeight: 800, fontSize: "12px",
                  fontFamily: "JetBrains Mono, monospace",
                  boxShadow: "0 0 4px rgba(0,0,0,0.6)",
                },
              },
              label,
            ),
          ),
        ),
      );
    });
  });

  // Invisible raycast target — a wide horizontal plane at the ceiling mount Y.
  const plane = h("mesh", {
    ref: planeRef, visible: false,
    position: [room_L_in * IN_TO_M / 2, ceilingMountY, room_W_in * IN_TO_M / 2],
    rotation: [-Math.PI / 2, 0, 0],
  },
    h("planeGeometry", { args: [room_L_in * IN_TO_M * 2, room_W_in * IN_TO_M * 2] }),
    h("meshBasicMaterial", null),
  );

  return h("group", null, plane, ...handles);
}

/* ─── ConduitLayer ──────────────────────────────────────────────── */
/* ConduitLayer + its CONDUIT_COLORS palette moved to ./conduits.js
   (iter 40 extraction — kept scene.js under the 1500-line ceiling).
   Imported at the top of this file so the local `ConduitLayer` binding
   stays valid; also re-exported there for back-compat with consumers
   that import `{ ConduitLayer }` from "./scene". */

/* ─── MeasurementLine ───────────────────────────────────────────── */
/* ─── MeasurementLine ───────────────────────────────────────────── */
/* MeasurementLine + CameraFocus moved to ./measurement.js (iter 42
   refactor). Imported below and re-exported so existing consumers of
   `{ MeasurementLine, CameraFocus } from "./scene"` keep working. */
import { MeasurementLine as MeasurementLineImpl, CameraFocus as CameraFocusImpl } from "./measurement";
const MeasurementLine = MeasurementLineImpl;
const CameraFocus = CameraFocusImpl;
export { MeasurementLineImpl as MeasurementLine, CameraFocusImpl as CameraFocus };

/* ─── FeederLines ──────────────────────────────────────────────── */
/* Dashed grey overlay polylines from every wired equipment to its declared
   feeder source, like a single-line-diagram peek without opening the cable
   schedule. Both endpoints anchor to the equipment centre at its mount Y so
   the lines float through the room rather than skimming the floor. Each line
   is clickable — clicking it fires onFeederClick(dstTag) so the parent can
   open the Cables tab and scroll/highlight the matching row. */
export function FeederLines({ items, onFeederClick }) {
  const [hover, setHover] = useState(null); // {key, srcTag, dstTag, mid}
  const placed = (items || []).filter(
    (it) => it.position && it.fed_from_tag,
  );
  if (!placed.length) return null;
  const byTag = new Map();
  (items || []).forEach((it) => {
    if (it.position && it.tag) byTag.set(it.tag, it);
  });

  const centerOf = (it) => {
    const p = it.position || {};
    const w = p.w_in || it.length_in || 24;
    const d = p.h_in || it.width_in  || 18;
    const hEq = it.depth_in || 84;
    const mount = Math.max(0, Number(it.mount_height_in) || 0);
    return [
      (p.x_in + w / 2) * IN_TO_M,
      (mount + hEq / 2) * IN_TO_M,
      (p.y_in + d / 2) * IN_TO_M,
    ];
  };

  const segments = [];
  placed.forEach((dst) => {
    const src = byTag.get(dst.fed_from_tag);
    if (!src) return;
    const a = centerOf(src);
    const b = centerOf(dst);
    const mid = [(a[0] + b[0]) / 2, Math.max(a[1], b[1]) + 0.5, (a[2] + b[2]) / 2];
    segments.push({
      key: `${src.id}->${dst.id}`,
      points: [a, mid, b],
      mid,
      srcTag: src.tag,
      dstTag: dst.tag,
    });
  });
  if (!segments.length) return null;

  return h("group", { name: "feeder-lines" },
    ...segments.map((s) => {
      const isHover = hover && hover.key === s.key;
      return h(Line, {
        key: s.key,
        points: s.points,
        color: isHover ? "#00E5FF" : "#FFCC00",
        lineWidth: isHover ? 2.5 : 1.4,
        dashed: true,
        dashSize: 0.12,
        gapSize: 0.06,
        transparent: true,
        opacity: isHover ? 1 : 0.75,
        onPointerOver: (e) => {
          e.stopPropagation();
          document.body.style.cursor = "pointer";
          setHover(s);
        },
        onPointerOut: () => {
          document.body.style.cursor = "auto";
          setHover((cur) => cur && cur.key === s.key ? null : cur);
        },
        onClick: (e) => {
          e.stopPropagation();
          if (typeof onFeederClick === "function") onFeederClick(s.dstTag);
        },
      });
    }),
    // Hover tooltip — anchored to the line's mid-point.
    hover ? h(Html, {
      position: hover.mid,
      center: true,
      style: { pointerEvents: "none", zIndex: 50 },
    },
      React.createElement("div", {
        style: {
          background: "rgba(10,13,18,0.95)",
          color: "#FFCC00",
          fontFamily: "ui-monospace, monospace",
          fontSize: 10,
          padding: "3px 8px",
          border: "1px solid #FFCC00",
          whiteSpace: "nowrap",
        },
      },
        `${hover.srcTag} → ${hover.dstTag} · click to open in Cables tab`,
      ),
    ) : null,
  );
}


/* ─── v1.84 — Interior partitions / segments ───────────────────────
 * Each partition is a free-form straight line in plan coords
 *   {x1,y1,x2,y2}  (inches)
 * with a required door cutout and (later) tray penetrations. Rendered
 * as 3 boxes per partition: pre-door wall · header above door · post-
 * door wall. Door cutout achieved without CSG. */
function PartitionMesh({ partition, room }) {
  const { x1, y1, x2, y2 } = partition;
  const dxIn = x2 - x1, dyIn = y2 - y1;
  const lenIn = Math.hypot(dxIn, dyIn);
  if (lenIn < 1) return null;
  const angle = Math.atan2(dyIn, dxIn);
  const wallHIn = partition.height_in || room.depth_in || 96;
  const thickIn = 4;                                 // 4″ partition thickness
  // v2.13 — doors are now an ARRAY. Legacy single-door shape is migrated
  // on the fly into a one-element list so old projects still render.
  const doorsArr = Array.isArray(partition.doors) && partition.doors.length
    ? partition.doors
    : (partition.door ? [partition.door] : []);
  const doorsIn = doorsArr.map((d) => {
    const off = Math.max(0, Math.min(1, d.offset ?? 0.5));
    const dW  = d.width_in  || 36;
    const dH  = d.height_in || 84;
    const c = lenIn * off;
    return {
      kind: "door",
      startIn: Math.max(0, c - dW / 2),
      endIn:   Math.min(lenIn, c + dW / 2),
      headIn:  dH,
    };
  });
  // v2.11 — windows.
  const windowsIn = (partition.windows || []).map((w) => {
    const off  = Math.max(0, Math.min(1, w.offset ?? 0.5));
    const wW   = w.width_in  || 24;
    const wH   = w.height_in || 36;
    const sill = Math.max(0, w.sill_in || 36);
    const c = lenIn * off;
    return {
      kind: "window",
      startIn: Math.max(0, c - wW / 2),
      endIn:   Math.min(lenIn, c + wW / 2),
      sillIn:  sill,
      headIn:  sill + wH,
    };
  });
  const openings = [...doorsIn, ...windowsIn].sort((a, b) => a.startIn - b.startIn);
  const wallH_m = wallHIn * IN_TO_M;
  const thick_m = thickIn * IN_TO_M;
  const startWorld = [x1 * IN_TO_M, 0, y1 * IN_TO_M];
  const mat = h("meshStandardMaterial", {
    color: "#3A4049", metalness: 0.15, roughness: 0.75,
  });
  const fullBlock = (sIn, eIn, keyHint) => {
    const w = eIn - sIn;
    if (w < 0.1) return null;
    return h("mesh", {
      key: keyHint,
      position: [(sIn + w / 2) * IN_TO_M, wallH_m / 2, 0],
      castShadow: false, receiveShadow: true,
    },
      h("boxGeometry", { args: [w * IN_TO_M, wallH_m, thick_m] }),
      mat,
      h(Edges, { color: "#8B8F97", threshold: 15 }),
    );
  };
  const children = [];
  // Solid segments between openings
  let cursor = 0;
  openings.forEach((op, i) => {
    if (op.startIn > cursor + 0.1) {
      children.push(fullBlock(cursor, op.startIn, `solid-${i}`));
    }
    cursor = Math.max(cursor, op.endIn);
  });
  if (cursor < lenIn - 0.1) {
    children.push(fullBlock(cursor, lenIn, "solid-end"));
  }
  // Doors: header above each opening + orange frame trim.
  doorsIn.forEach((d, di) => {
    const wIn = d.endIn - d.startIn;
    if (wIn < 0.1) return;
    const cx = (d.startIn + wIn / 2) * IN_TO_M;
    const dH = d.headIn;
    const headerH = Math.max(0, wallHIn - dH);
    if (headerH > 0.1) {
      children.push(h("mesh", {
        key: `door-${di}-header`,
        position: [cx, dH * IN_TO_M + (headerH / 2) * IN_TO_M, 0],
        castShadow: false, receiveShadow: true,
      },
        h("boxGeometry", { args: [wIn * IN_TO_M, headerH * IN_TO_M, thick_m] }),
        mat,
        h(Edges, { color: "#8B8F97", threshold: 15 }),
      ));
    }
    // Door frame trim
    children.push(h("mesh", {
      key: `door-${di}-frame`,
      position: [cx, (dH * IN_TO_M) / 2, 0],
    },
      h("boxGeometry", { args: [wIn * IN_TO_M, dH * IN_TO_M, thick_m * 0.25] }),
      h("meshStandardMaterial", { color: "#FF9F0A", opacity: 0.15, transparent: true,
                                    metalness: 0.4, roughness: 0.5,
                                    emissive: "#3a2300", emissiveIntensity: 0.5 }),
      h(Edges, { color: "#FF9F0A", threshold: 15 }),
    ));
  });
  // Windows: sill (below) + header (above) + transparent glazing.
  windowsIn.forEach((w, i) => {
    const wIn = w.endIn - w.startIn;
    if (wIn < 0.1) return;
    const cx = (w.startIn + wIn / 2) * IN_TO_M;
    if (w.sillIn > 0.1) {
      children.push(h("mesh", {
        key: `win-${i}-sill`,
        position: [cx, (w.sillIn / 2) * IN_TO_M, 0],
        castShadow: false, receiveShadow: true,
      },
        h("boxGeometry", { args: [wIn * IN_TO_M, w.sillIn * IN_TO_M, thick_m] }),
        mat,
        h(Edges, { color: "#8B8F97", threshold: 15 }),
      ));
    }
    const headerStartIn = w.headIn;
    const headerHIn = Math.max(0, wallHIn - headerStartIn);
    if (headerHIn > 0.1) {
      children.push(h("mesh", {
        key: `win-${i}-head`,
        position: [cx, (headerStartIn + headerHIn / 2) * IN_TO_M, 0],
        castShadow: false, receiveShadow: true,
      },
        h("boxGeometry", { args: [wIn * IN_TO_M, headerHIn * IN_TO_M, thick_m] }),
        mat,
        h(Edges, { color: "#8B8F97", threshold: 15 }),
      ));
    }
    const glazingH = (w.headIn - w.sillIn);
    if (glazingH > 0.1) {
      children.push(h("mesh", {
        key: `win-${i}-glass`,
        position: [cx, (w.sillIn + glazingH / 2) * IN_TO_M, 0],
      },
        h("boxGeometry", { args: [wIn * IN_TO_M, glazingH * IN_TO_M, thick_m * 0.2] }),
        h("meshStandardMaterial", { color: "#00E5FF", opacity: 0.22, transparent: true,
                                      metalness: 0.3, roughness: 0.1,
                                      emissive: "#003a45", emissiveIntensity: 0.6 }),
        h(Edges, { color: "#00E5FF", threshold: 15 }),
      ));
    }
  });
  return h("group", {
    position: startWorld,
    rotation: [0, -angle, 0],
  }, ...children);
}

function PartitionsLayer({ project, room }) {
  const partitions = project?.room_partitions || [];
  if (!partitions.length) return null;
  return h("group", null,
    ...partitions.map((p, i) => h(PartitionMesh, { key: p.id || `part-${i}`, partition: p, room })),
  );
}

/* ─── Top-level Scene ───────────────────────────────────────────── */
export function Scene({
  project, complianceById, onDragEnd, selected, setSelected,
  snap, measureMode, measurePicks, onMeasurePick, focusTarget,
  itemsToRender, showTrays, showConduits, showDimensions,
  showFeederLines, onFeederClick,
  selectedTrayId, setSelectedTrayId, viewLocked, fillBySegment,
  showShell, showWallGrids, openings, showWorkingSpace,
  hiddenWalls, onToggleWall,
  dragging, setDragging, onMoveTrayEndpoint, equipmentLocked,
  onEquipmentContextMenu, onTrayContextMenu,
}) {
  const dragPlaneRef = useRef();
  const orbitRef = useRef();
  const room = {
    length_in: project.length_in || 240,
    width_in:  project.width_in  || 180,
    depth_in:  project.depth_in  || 96,
    basement_depth_in: project.basement_depth_in || 0,
  };
  const L = room.length_in * IN_TO_M;
  const W = room.width_in  * IN_TO_M;
  const H = room.depth_in  * IN_TO_M;
  const camTarget = [L / 2, H / 4, W / 2];
  const selectedItem = showDimensions && selected
    ? itemsToRender.find((i) => i.id === selected && !i._unplaced)
    : null;
  const placedItems = itemsToRender.filter((i) => !i._unplaced);

  // Imperative orbit-suppression — fires synchronously when the user grabs an
  // equipment box, so any rotate gesture OrbitControls may have started on the
  // same pointerdown is aborted instantly.
  const suppressOrbit = () => {
    setDragging && setDragging(true);
    if (orbitRef.current) {
      orbitRef.current.enabled = false;
    }
  };
  const releaseOrbit = () => {
    setDragging && setDragging(false);
    if (orbitRef.current && !viewLocked) {
      orbitRef.current.enabled = true;
    }
  };

  return h(React.Fragment, null,
    h("color", { attach: "background", args: ["#050608"] }),
    h("ambientLight", { intensity: 0.6 }),
    h("directionalLight", { position: [L, H * 2, W], intensity: 0.9, castShadow: true }),
    h(Room, {
      room, dragPlaneRef, snap,
      showShell, showWallGrids, openings,
      hiddenWalls, onToggleWall,
    }),
    h(PartitionsLayer, { project, room }),
    showWorkingSpace ? h(WorkingSpaceLayer, {
      items: placedItems, voltage: project.voltage, room,
    }) : null,
    showTrays    ? h(TrayLayer,    { project, trayDefaults: project.tray_defaults,
                                      selectedTrayId, setSelectedTrayId,
                                      exploded: !!selectedTrayId, fillBySegment,
                                      onTrayContextMenu }) : null,
    showTrays && onMoveTrayEndpoint ? h(TrayHandlesLayer, {
      project, onMoveEndpoint: onMoveTrayEndpoint, selectedTrayId,
      setOrbitEnabled: (en) => { if (orbitRef.current) orbitRef.current.enabled = en && !viewLocked; },
    }) : null,
    showConduits ? h(ConduitLayer, { project }) : null,
    showFeederLines ? h(FeederLines, { items: placedItems, onFeederClick }) : null,
    selectedItem ? h(DimensionOverlay, { item: selectedItem, room, allItems: placedItems }) : null,
    ...itemsToRender.map((it) =>
      h(EquipmentBox, {
        key: it.id, item: it, room, onDragEnd, selected, setSelected,
        dragPlaneRef, complianceById, snap, measureMode, onMeasurePick,
        onDragStart: equipmentLocked ? () => {} : suppressOrbit,
        onDragStop:  releaseOrbit,
        locked: !!equipmentLocked,
        onContextMenu: onEquipmentContextMenu,
      }),
    ),
    measurePicks[0] && measurePicks[1]
      ? h(MeasurementLine, { a: measurePicks[0], b: measurePicks[1] })
      : null,
    h(CameraFocus, { focusTarget }),
    h(OrbitControls, {
      ref: orbitRef,
      target: camTarget, enableDamping: true, dampingFactor: 0.1,
      minDistance: 0.5, maxDistance: 80,
      // The user wants the camera to orbit when they drag the mouse on empty
      // canvas. Equipment drag is handled by EquipmentBox's onPointerDown,
      // which calls suppressOrbit() → kills OrbitControls' active gesture
      // synchronously. Pointer-up re-enables orbit.
      enabled: !viewLocked && !dragging,
      mouseButtons: {
        LEFT: THREE.MOUSE.ROTATE,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.PAN,
      },
    }),
  );
}
